// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

// material ui icons
import CloudRoundedIcon from '@mui/icons-material/CloudRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';

// material ui
import {
  alpha,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { useGetEnergySavingsQuery } from '@/api/energy-analysis/calculateEnergySavingsApiSlice';
import {
  useGetEnergyGroupInitiativeQuery,
  useGetEnergyGroupQuery,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { WritableOnly } from '@/components/access-control';
import PageContainer from '@/components/layout/PageContainer';
import CardHeaderWithIcon from '@/components/shared/surfaces/card/CardHeaderWithIcon';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import InitiativeChart from '@/features/energyxplorer/components/initiatives/InitiativeChart';

// components
import { useBreadcrumb } from '@/hooks/useBreadcrumb';
import { formatNumber } from '@/utils/formatters';
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/deepdive/$energyGroupId/energy-initiatives/$initiativeId/',
)({
  component: RouteComponent,
  staticData: { crumb: 'gloassry:details' },
});

function RouteComponent() {
  const { t } = useTranslation('energyxplorer-initiatives-tab');

  const { accountId, energyGroupId, initiativeId } = useParams({
    strict: false,
  });

  const {
    data: energyGroup,
    isLoading: isLoadingEnergyGroup,
    isFetching: isFetchingEnergyGroup,
  } = useGetEnergyGroupQuery(
    { groupId: Number(energyGroupId) },
    {
      skip: !energyGroupId,
    },
  );

  const {
    data: initiative,
    isLoading: isLoadingInitiative,
    isFetching: isFetchingInitiative,
  } = useGetEnergyGroupInitiativeQuery(
    {
      groupId: Number(energyGroupId),
      initiativeId: Number(initiativeId),
    },
    {
      skip: !energyGroupId || !initiativeId,
    },
  );

  const {
    data: energySavingsData,
    isLoading: isLoadingEnergySavings,
    isFetching: isFetchingEnergySavings,
  } = useGetEnergySavingsQuery(
    {
      accountId: Number(accountId),
      energyGroupId: Number(energyGroupId),
    },
    {
      skip: !energyGroupId || !accountId,
    },
  );

  const savings = energySavingsData?.InitiativeEnergySavings || [];

  // Create a lookup map from InitiativeEnergySavings
  const savingsMap = new Map(savings.map((s) => [s.Id, s]));

  const enrichedInitiative = savingsMap.has(Number(initiativeId))
    ? {
        ...initiative,
        savings: savingsMap.get(Number(initiativeId)),
      }
    : initiative;

  const [mode, setMode] = useState(
    enrichedInitiative?.savings?.AdvancedModelUsed ? 'advanced' : 'simple',
  );

  const energySavings =
    mode === 'advanced'
      ? enrichedInitiative?.savings?.AdvancedEnergySavings?.EnergySavingsSummary
          ?.EnergySavings
      : enrichedInitiative?.savings?.BasicEnergySavings?.EnergySavingsSummary
          ?.EnergySavings;

  const energySavingsPercentage =
    mode === 'advanced'
      ? enrichedInitiative?.savings?.AdvancedEnergySavings?.EnergySavingsSummary
          ?.EnergySavingsPercentage
      : enrichedInitiative?.savings?.BasicEnergySavings?.EnergySavingsSummary
          ?.EnergySavingsPercentage;

  const co2eSavings =
    mode === 'advanced'
      ? enrichedInitiative?.savings?.AdvancedEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavings
      : enrichedInitiative?.savings?.BasicEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavings;

  const co2eSavingsPercentage =
    mode === 'advanced'
      ? enrichedInitiative?.savings?.AdvancedEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavingsPercentage
      : enrichedInitiative?.savings?.BasicEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavingsPercentage;

  const getTrendColor = (percentage) => (percentage >= 0 ? 'success' : 'error');

  // Map the initiative's included node guids to their full node objects
  // (name + type) from the energy group, so they can be rendered as chips.
  const energyTreeNodeById = new Map(
    (energyGroup?.energyTreeNodes ?? []).map((node) => [
      node.energyTreeNodeId,
      node,
    ]),
  );
  const includedNodes = (initiative?.energyTreeNodes ?? [])
    .map((nodeId) => energyTreeNodeById.get(nodeId))
    .filter(Boolean);

  useBreadcrumb(initiative?.name || null);

  return (
    <>
      <GatewayLoadingIndicator
        isLoading={
          isLoadingInitiative ||
          isFetchingInitiative ||
          isLoadingEnergySavings ||
          isFetchingEnergySavings ||
          isLoadingEnergyGroup ||
          isFetchingEnergyGroup
        }
      />
      <PageContainer sx={{ pt: 0 }} data-testid="edit-initiative-page">
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size="grow">
            <Card sx={{ borderRadius: 2, p: 2 }}>
              <CardHeaderWithIcon
                icon={<EnergySavingsLeafRoundedIcon />}
                title={
                  initiative?.state === 'Archived'
                    ? t('chart.energy_savings')
                    : 'Nuværende energibesparelse'
                }
                tooltip={t('chart.energy_savings_description')}
              />
              <Typography
                variant="h4"
                color="textPrimary"
                fontWeight="bold"
                gutterBottom
              >
                {energySavings ? `${formatNumber(energySavings)} kWh` : '0 kWh'}
                <Chip
                  variant="filled"
                  sx={{
                    marginLeft: 1,
                    mb: 1,
                    borderRadius: 2,
                    backgroundColor: (theme) =>
                      alpha(
                        theme.palette[getTrendColor(energySavingsPercentage)]
                          .main,
                        0.1,
                      ),
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette[getTrendColor(energySavingsPercentage)]
                            .dark
                        : theme.palette[getTrendColor(energySavingsPercentage)]
                            .light,
                  }}
                  label={
                    energySavingsPercentage
                      ? `${energySavingsPercentage.toFixed(1)}%`
                      : '0%'
                  }
                  size="small"
                />
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('chart.energy_savings_for')}{' '}
                {initiative?.name || 'valgt initiativ'}
              </Typography>
            </Card>
          </Grid>

          {initiative?.state === 'Archived' && (
            <Grid size="grow">
              <Card sx={{ borderRadius: 2, p: 2 }}>
                <CardHeaderWithIcon
                  icon={<CloudRoundedIcon />}
                  title={t('chart.co2e_reduction')}
                  tooltip={t('chart.co2e_reduction_description')}
                />
                <Typography
                  variant="h4"
                  color="textPrimary"
                  fontWeight="bold"
                  gutterBottom
                >
                  {co2eSavings
                    ? `${formatNumber(co2eSavings / 1000)} kg CO2e`
                    : '0 kg CO2e'}
                  <Chip
                    variant="filled"
                    sx={{
                      marginLeft: 1,
                      mb: 1,
                      borderRadius: 2,
                      backgroundColor: (theme) =>
                        alpha(
                          theme.palette[getTrendColor(co2eSavingsPercentage)]
                            .main,
                          0.1,
                        ),
                      color: (theme) =>
                        theme.palette.mode === 'light'
                          ? theme.palette[getTrendColor(co2eSavingsPercentage)]
                              .dark
                          : theme.palette[getTrendColor(co2eSavingsPercentage)]
                              .light,
                    }}
                    label={
                      co2eSavingsPercentage
                        ? `${co2eSavingsPercentage.toFixed(1)}%`
                        : '0%'
                    }
                    size="small"
                  />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('chart.co2e_reduction_for')}{' '}
                  {initiative?.name || 'valgt initiativ'}
                </Typography>
              </Card>
            </Grid>
          )}
        </Grid>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ mb: 2 }}
        >
          {includedNodes.map((node) => (
            <Tooltip
              key={node.energyTreeNodeId}
              title={node.type}
              placement="top"
            >
              <Chip
                label={node.name}
                size="small"
                icon={
                  <i
                    className={`fa-solid ${getTypeIcon(node.type)}`}
                    style={{ color: 'white', fontSize: '0.7rem' }}
                  />
                }
                sx={{
                  backgroundColor: getTypeColor(node.type),
                  color: 'white',
                  borderRadius: 2,
                }}
              />
            </Tooltip>
          ))}
        </Stack>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            // size="small"
            onChange={(_event, newMode) => {
              if (newMode) {
                setMode(newMode);
              }
            }}
          >
            <ToggleButton value="simple">
              {t('cards.simple_model_used.title')}
            </ToggleButton>
            <Tooltip
              title={
                enrichedInitiative?.savings?.AdvancedModelUsed
                  ? t('cards.advanced_model_used.description')
                  : t('cards.advanced_model_not_available')
              }
            >
              <span>
                <ToggleButton
                  value="advanced"
                  disabled={!enrichedInitiative?.savings?.AdvancedModelUsed}
                >
                  {t('cards.advanced_model_used.title')}
                </ToggleButton>
              </span>
            </Tooltip>
          </ToggleButtonGroup>
          <WritableOnly>
            <Tooltip title={t('edit_initiative')}>
              <Button
                component={Link}
                variant="outlined"
                to="edit"
                search={(prev: Record<string, string>) => ({ ...prev })}
                startIcon={<EditRoundedIcon />}
              >
                {t('common:actions.edit')}
              </Button>
            </Tooltip>
          </WritableOnly>
        </Box>

        <InitiativeChart selectedInitiative={enrichedInitiative} mode={mode} />
      </PageContainer>
    </>
  );
}
